import { Controller, useForm } from 'react-hook-form';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { SelectButton } from 'primereact/selectbutton';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
} from 'primereact/autocomplete';
import { ReactComponent as Arrow } from '$icons/uncolored/arrow.svg';
import { useEffect, useMemo, useState } from 'react';
import SPKToast from '$components/SPKToast/SPKToast';
import { companySlice, useLoadCompaniesQuery } from '$store/modules/companies';
import { useAppDispatch, useAppSelector } from '$hooks/index';
import { useTranslation } from 'react-i18next';
import { useGetPublicJobPositionsQuery } from '$store/modules/form-info';
import { useGetAllLanguagesQuery } from '$store/modules/language';
import {
  MINIMUM_BASE_COMPENSATION,
  getTenantDefaultFee,
} from '$constants/SimeraOperations';
import {
  TypesOfWork,
  IdealStartDateValues,
  WorkLocationTypes,
} from '$types/position/positionPropertyValues';
import { useGetAllCountriesQuery } from '$store/modules/countries';
import { useGetAllTenantsQuery } from '$store/modules/tenant';
import CompanyDto from '$types/serverTypes/CompanyDto';

const SPKPositionOverviewStep = ({
  onComplete,
  isInternal,
}: {
  onComplete: () => void;
  isInternal?: boolean;
}) => {
  const [t] = useTranslation('global');
  const [isOpenJobTypes, setIsOpenJobTypes] = useState(false);
  const [isOpenIdealStartDate, setIsOpenIdealStartDate] = useState(false);
  const [isOpenCountry, setIsOpenCountry] = useState(false);
  const [companySuggestions, setCompanySuggestions] = useState<CompanyDto[]>(
    []
  );

  const { data: JobPositionsData, isLoading: isLoadingGetJobPositions } =
    useGetPublicJobPositionsQuery();
  const { data: languagesData = [] } = useGetAllLanguagesQuery();
  const { data: countriesData = [] } = useGetAllCountriesQuery();
  const { data: companiesData } = useLoadCompaniesQuery(undefined, {
    skip: !isInternal,
  });
  const { data: tenantData = [] } = useGetAllTenantsQuery(undefined, {
    skip: !isInternal,
  });
  const dispatch = useAppDispatch();
  const { employerAddPositionForm } = useAppSelector((state) => state.company);
  const { locale, currencyCode, usdToCurrencyConverter, currency } =
    useAppSelector((state) => state.brand);
  const {
    SalaryUpperBound,
    SalaryLowerBound,
    jobTitle,
    JobPosition,
    jobIsntListed,
    IdealStartDate,
    TypeOfWork,
    customJobType,
    Languages,
    Country,
    WorkLocation,
    Company,
    DealId,
    TenantId,
    FeeAmount,
  } = employerAddPositionForm;

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, dirtyFields },
    watch,
  } = useForm({
    defaultValues: {
      SalaryUpperBound,
      SalaryLowerBound,
      jobTitle,
      JobPosition,
      jobIsntListed,
      IdealStartDate,
      TypeOfWork,
      customJobType,
      Languages,
      Country,
      WorkLocation,
      Company: Company || null,
      DealId: DealId || '',
      TenantId: TenantId && TenantId >= 0 ? TenantId : undefined,
      FeeAmount: FeeAmount ?? (isInternal ? null : getTenantDefaultFee()),
    },
  });

  const onSubmitHandler = (data: any) => {
    dispatch(companySlice.actions.employerAddPositionOverview(data));
    onComplete();
  };

  const onSubmitErrorHandler = () => {
    SPKToast(t('EmployerAddPosition.Errors.MissingRequiredFields'), {
      type: 'Error',
    });
  };

  const jobIsNotListedCheckbox = watch('jobIsntListed');
  const SalaryLowerBoundValue = watch('SalaryLowerBound');
  const tenantIdValue = watch('TenantId');

  const tenantDefaultFee = useMemo(() => {
    const tenantName = tenantData.find(
      (singleTenant) => singleTenant.Id === tenantIdValue
    )?.title;
    return getTenantDefaultFee(tenantName);
  }, [tenantData, tenantIdValue]);

  useEffect(() => {
    if (!isInternal) return;
    if (dirtyFields.FeeAmount) return;
    setValue('FeeAmount', tenantDefaultFee, { shouldDirty: false });
  }, [isInternal, tenantDefaultFee, dirtyFields.FeeAmount, setValue]);

  const minimumCompensationConverted = useMemo(() => {
    return usdToCurrencyConverter(MINIMUM_BASE_COMPENSATION);
  }, [usdToCurrencyConverter]);

  const autocompleteCompanySearch = ({ query }: AutoCompleteCompleteEvent) => {
    const matched =
      companiesData?.filter((opt) =>
        opt.Name.toLowerCase().includes(query.toLowerCase())
      ) ?? [];
    setCompanySuggestions(matched);
  };

  return (
    <>
      <h1 className="text-4xl font-extrabold mb-8">
        {t('EmployerAddPosition.PositionDetails')}
      </h1>
      <form
        onSubmit={handleSubmit(onSubmitHandler, onSubmitErrorHandler)}
        className="w-full flex flex-col gap-6"
      >
        {/* Company (internal only) */}
        {isInternal && (
          <div className="flex flex-col gap-1">
            <label
              htmlFor="Company"
              className="text-spk-mediumContrast text-xs"
            >
              {t('EmployerAddPosition.Company')}
            </label>
            <Controller
              control={control}
              name="Company"
              rules={{
                validate: (e) =>
                  !!(e as CompanyDto)?.Name ||
                  t('EmployerAddPosition.Errors.RequiredField'),
              }}
              render={({ field }) => (
                <AutoComplete
                  {...field}
                  id="Company"
                  className="w-full"
                  placeholder={t('EmployerAddPosition.CompanyNamePlaceholder')}
                  forceSelection
                  suggestions={companySuggestions}
                  completeMethod={autocompleteCompanySearch}
                  field="Name"
                  pt={{
                    input: {
                      root: {
                        className: `w-full ${
                          errors.Company?.message
                            ? '!border-red-400 !shadow-none'
                            : 'enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3]'
                        }`,
                      },
                    },
                  }}
                />
              )}
            />
            {errors.Company?.message && (
              <span className="text-spk-brightRed text-xs">
                *{errors.Company.message as string}
              </span>
            )}
          </div>
        )}

        {/* Deal ID + Tenant (internal only) */}
        {isInternal && (
          <div className="flex flex-row gap-4 w-full">
            <div className="flex flex-col gap-1 w-full">
              <label
                htmlFor="DealId"
                className="text-spk-mediumContrast text-xs"
              >
                {t('EmployerAddPosition.DealId')}
              </label>
              <Controller
                control={control}
                name="DealId"
                render={({ field }) => (
                  <InputText
                    {...field}
                    id="DealId"
                    placeholder={t('EmployerAddPosition.DealId')}
                    autoComplete="off"
                    className="w-full enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3]"
                  />
                )}
              />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <label
                htmlFor="TenantId"
                className="text-spk-mediumContrast text-xs"
              >
                {t('EmployerAddPosition.Organization')}
              </label>
              <Controller
                control={control}
                name="TenantId"
                rules={{
                  required: t('EmployerAddPosition.Errors.RequiredField'),
                }}
                render={({ field }) => (
                  <>
                    <Dropdown
                      {...field}
                      id="TenantId"
                      className="w-full"
                      options={tenantData}
                      optionLabel="title"
                      optionValue="Id"
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.SelectAnOption'
                      )}
                      pt={{
                        root: {
                          className: `shadow-none ${
                            errors.IdealStartDate?.message
                              ? '!border-red-400'
                              : 'enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3] focus-within:!border-[#2196f3]'
                          }`,
                        },
                      }}
                    />
                    {errors.TenantId?.message && (
                      <span className="text-spk-brightRed text-xs">
                        *{errors.TenantId.message as string}
                      </span>
                    )}
                  </>
                )}
              />
            </div>
          </div>
        )}

        {/* Job Title */}
        <div className="flex flex-col">
          <label htmlFor="jobTitle" className="text-spk-mediumContrast text-xs">
            {t('EmployerAddPosition.JobTitleOptional')}
          </label>
          <Controller
            control={control}
            name="jobTitle"
            render={({ field }) => (
              <InputText
                className="w-full my-1 enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3]"
                placeholder={t('EmployerAddPosition.Placeholders.JobTitle')}
                {...field}
                autoComplete="off"
              />
            )}
          />
          <small id="jobTitle-description" className="text-spk-mediumContrast">
            {t('EmployerAddPosition.JobTitleDescription')}
          </small>
        </div>

        {/* Job Type */}
        <div className="flex flex-col">
          <label
            htmlFor="JobPosition"
            className="text-spk-mediumContrast text-xs"
          >
            {t('EmployerAddPosition.JobType')}
          </label>
          <Controller
            control={control}
            name="JobPosition"
            rules={{
              required:
                !jobIsNotListedCheckbox &&
                t('EmployerAddPosition.Errors.JobTypeRequired'),
            }}
            render={({ field }) => (
              <div
                className={`relative w-full flex flex-row items-center ${
                  jobIsNotListedCheckbox && 'hover:cursor-not-allowed'
                }`}
              >
                <Dropdown
                  loading={isLoadingGetJobPositions}
                  className="my-1 w-full"
                  placeholder={t('EmployerAddPosition.Placeholders.SelectOne')}
                  options={JobPositionsData}
                  optionLabel="title"
                  // Temporaly disabled since there are only 20 items at this moment
                  // virtualScrollerOptions={{ itemSize: 14 }}
                  onShow={() => setIsOpenJobTypes(true)}
                  onHide={() => setIsOpenJobTypes(false)}
                  disabled={jobIsNotListedCheckbox}
                  pt={{
                    panel: {
                      className: '!mt-20',
                    },
                    trigger: {
                      className: 'hidden',
                    },
                    root: {
                      className: `${
                        errors.JobPosition?.message
                          ? '!border-red-400'
                          : 'enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3] focus-within:!border-[#2196f3]'
                      } ${jobIsNotListedCheckbox && 'opacity-45'}`,
                    },
                  }}
                  {...field}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                />
                <Arrow
                  className={`fill-employer-primary w-4 h-4 absolute z-50 right-4 transition-transform ${
                    isOpenJobTypes ? 'rotate-180' : 'rotate-0'
                  } ${
                    jobIsNotListedCheckbox && 'opacity-45'
                  } pointer-events-none`}
                />
              </div>
            )}
          />
          {errors.JobPosition?.message && (
            <span className="text-spk-brightRed text-xs">
              *{errors.JobPosition?.message}
            </span>
          )}
          <small id="jobTitle-description" className="text-spk-mediumContrast">
            {t('EmployerAddPosition.JobTypeDescription')}
          </small>

          <div className="flex flex-row items-center mt-4 gap-2">
            <Controller
              control={control}
              name="jobIsntListed"
              render={({ field }) => (
                <Checkbox
                  inputId="jobIsntListed"
                  title={t('EmployerAddPosition.JobTypeIsntListed')}
                  checked={field.value}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.checked);
                  }}
                />
              )}
            />
            <label
              htmlFor="jobIsntListed"
              className="text-spk-mediumContrast text-sm"
            >
              {t('EmployerAddPosition.JobTypeIsntListed')}
            </label>
          </div>
          {jobIsNotListedCheckbox && (
            <Controller
              control={control}
              name="customJobType"
              rules={{
                required:
                  jobIsNotListedCheckbox &&
                  t('EmployerAddPosition.JobTypeIsntListedCheckbox'),
              }}
              render={({ field }) => (
                <>
                  <InputText
                    className={`w-full mt-2 ${
                      errors.customJobType?.message &&
                      '!border-red-600 !shadow-none'
                    } enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3]`}
                    placeholder="Enter your job type here"
                    {...field}
                    autoComplete="off"
                  />
                  {errors.customJobType?.message && (
                    <span className="text-spk-brightRed text-xs">
                      *{errors.customJobType?.message}
                    </span>
                  )}
                </>
              )}
            />
          )}
        </div>

        {/* Type of work */}
        <div>
          <label
            htmlFor="TypeOfWork"
            className="text-spk-mediumContrast text-xs"
          >
            {t('EmployerAddPosition.TypeOfWork')}
          </label>
          <Controller
            control={control}
            name="TypeOfWork"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectButton
                allowEmpty={false}
                className="w-full my-1"
                options={Object.values(TypesOfWork)}
                {...field}
              />
            )}
          />
        </div>

        {/* Work location */}
        <div>
          <label
            htmlFor="WorkLocation"
            className="text-spk-mediumContrast text-xs"
          >
            {t('EmployerAddPosition.TypeOfWork')}
          </label>
          <Controller
            control={control}
            name="WorkLocation"
            render={({ field }) => (
              <SelectButton
                allowEmpty={false}
                className="w-full my-1"
                options={Object.values(WorkLocationTypes)}
                {...field}
              />
            )}
          />
        </div>
        <div className="flex flex-row gap-4 w-full">
          {/* Monthly Base Compensation */}
          <div className="w-full">
            <legend className="text-spk-mediumContrast text-xs">
              {t('EmployerAddPosition.MonthlyBaseCompensation')}
            </legend>
            <div
              className={`flex flex-row items-center my-1 ${
                (errors.SalaryLowerBound || errors.SalaryUpperBound) && 'mb-4'
              }`}
            >
              <div className="w-full relative flex flex-col">
                <Controller
                  control={control}
                  name="SalaryLowerBound"
                  rules={{
                    required: t('EmployerAddPosition.Errors.RequiredField'),
                    validate: (value) => {
                      if (value < minimumCompensationConverted)
                        return t(
                          'EmployerAddPosition.Errors.BelowSimeraMinimumCompensation',
                          {
                            minimumSimeraCompensation:
                              minimumCompensationConverted,
                            currency,
                          }
                        );
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <InputNumber
                      className="w-full"
                      mode="currency"
                      currency={currencyCode}
                      min={0}
                      locale={locale}
                      minFractionDigits={0}
                      maxFractionDigits={2}
                      {...field}
                      onValueChange={(e) => field.onChange(e.value)}
                      onChange={(e) => field.onChange(e.value)}
                      pt={{
                        input: {
                          root: {
                            className: `${
                              errors.SalaryLowerBound?.message
                                ? '!border-spk-brightRed enabled:focus:shadow-none focus:shadow-none focus-within:shadow-none'
                                : 'enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3]'
                            }`,
                          },
                        },
                      }}
                    />
                  )}
                />
                {errors.SalaryLowerBound?.message && (
                  <span className="text-spk-brightRed text-xs absolute -bottom-5">
                    *{errors.SalaryLowerBound?.message}
                  </span>
                )}
              </div>

              <div className="h-1 w-8 border-b border-employer-secondary" />

              <div className="w-full relative flex flex-col">
                <Controller
                  name="SalaryUpperBound"
                  control={control}
                  rules={{
                    required: t('EmployerAddPosition.Errors.RequiredField'),
                    validate: (value) => {
                      if (value <= SalaryLowerBoundValue)
                        return t(
                          'EmployerAddPosition.Errors.MustBeGreaterThanMinimum'
                        );
                      return true;
                    },
                  }}
                  render={({ field }) => {
                    return (
                      <InputNumber
                        className="w-full"
                        mode="currency"
                        currency={currencyCode}
                        min={0}
                        locale={locale}
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        {...field}
                        onValueChange={(e) => field.onChange(e.value)}
                        onChange={(e) => field.onChange(e.value)}
                        pt={{
                          input: {
                            root: {
                              className: `${
                                errors.SalaryUpperBound?.message
                                  ? '!border-spk-brightRed enabled:focus:shadow-none focus:shadow-none focus-within:shadow-none'
                                  : 'enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3]'
                              }`,
                            },
                          },
                        }}
                      />
                    );
                  }}
                />
                {errors.SalaryUpperBound?.message && (
                  <span className="text-spk-brightRed text-xs absolute -bottom-5">
                    *{errors.SalaryUpperBound?.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Simera Fee (internal only) */}
          {isInternal && (
            <div className="flex flex-col w-full">
              <label
                htmlFor="FeeAmount"
                className="text-spk-mediumContrast text-xs"
              >
                {t('EmployerAddPosition.SimeraFee')}
              </label>
              <div className="my-1 flex flex-row items-center">
                <Controller
                  control={control}
                  name="FeeAmount"
                  render={({ field }) => (
                    <InputNumber
                      {...field}
                      inputId="FeeAmount"
                      className="w-full"
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      min={0}
                      minFractionDigits={0}
                      maxFractionDigits={2}
                      placeholder={`$${tenantDefaultFee.toLocaleString(
                        'en-US'
                      )}`}
                      value={field.value ?? null}
                      onValueChange={(e) => field.onChange(e.value ?? null)}
                      onChange={(e) => field.onChange(e.value ?? null)}
                    />
                  )}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-row gap-4 w-full">
          {/* Country */}
          <div className="flex flex-col w-full">
            <label
              htmlFor="IdealStartDate"
              className="text-spk-mediumContrast text-xs"
            >
              {t('EmployerAddPosition.PositionCountry')}
            </label>
            <Controller
              control={control}
              name="Country"
              render={({ field }) => (
                <div className="flex flex-row items-center relative my-1 w-full">
                  <Dropdown
                    {...field}
                    className="my-1 w-full"
                    placeholder={t(
                      'EmployerAddPosition.Placeholders.SelectAnOption'
                    )}
                    options={countriesData}
                    optionLabel="NiceName"
                    optionValue="Id"
                    onShow={() => setIsOpenCountry(true)}
                    onHide={() => setIsOpenCountry(false)}
                    filter
                    pt={{
                      trigger: {
                        className: 'hidden',
                      },
                      root: {
                        className: `shadow-none enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3] focus-within:!border-[#2196f3]'
                        }`,
                      },
                    }}
                  />
                  <Arrow
                    className={`fill-employer-primary w-4 h-4 absolute z-50 right-4 transition-transform ${
                      isOpenCountry ? 'rotate-180' : 'rotate-0'
                    } pointer-events-none`}
                  />
                </div>
              )}
            />
            {errors.Country?.message && (
              <span className="text-spk-brightRed text-xs">
                *{errors.Country?.message}
              </span>
            )}
          </div>
          {/* Ideal Start Date */}
          <div className="flex flex-col w-full">
            <label
              htmlFor="IdealStartDate"
              className="text-spk-mediumContrast text-xs"
            >
              {t('EmployerAddPosition.IdeaStartDate')}
            </label>
            <Controller
              control={control}
              name="IdealStartDate"
              rules={{
                required: t('EmployerAddPosition.Errors.RequiredField'),
              }}
              render={({ field }) => (
                <div className="flex flex-row items-center relative my-1">
                  <Dropdown
                    className="my-1 w-full"
                    placeholder={t(
                      'EmployerAddPosition.Placeholders.SelectAnOption'
                    )}
                    onShow={() => setIsOpenIdealStartDate(true)}
                    onHide={() => setIsOpenIdealStartDate(false)}
                    options={Object.values(IdealStartDateValues)}
                    {...field}
                    pt={{
                      trigger: {
                        className: 'hidden',
                      },
                      root: {
                        className: `shadow-none ${
                          errors.IdealStartDate?.message
                            ? '!border-red-400'
                            : 'enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3] focus-within:!border-[#2196f3]'
                        }`,
                      },
                    }}
                  />
                  <Arrow
                    className={`fill-employer-primary w-4 h-4 absolute z-50 right-4 transition-transform ${
                      isOpenIdealStartDate ? 'rotate-180' : 'rotate-0'
                    } pointer-events-none`}
                  />
                </div>
              )}
            />
            {errors.IdealStartDate?.message && (
              <span className="text-spk-brightRed text-xs">
                *{errors.IdealStartDate?.message}
              </span>
            )}
          </div>
        </div>
        {/* Languages */}
        <div className="flex flex-col">
          <label
            htmlFor="Languages"
            className="text-spk-mediumContrast text-xs"
          >
            {t('EmployerAddPosition.Languages')}
          </label>
          <Controller
            control={control}
            name="Languages"
            render={({ field }) => (
              <MultiSelect
                {...field}
                className="w-full my-1"
                options={languagesData}
                optionLabel="Name"
                optionValue="Id"
                placeholder={t(
                  'EmployerAddPosition.Placeholders.SelectAnOption'
                )}
                display="chip"
                showClear={false}
                pt={{
                  header: {
                    className: 'hidden',
                  },
                }}
              />
            )}
          />
          {errors.Languages?.message && (
            <span className="text-spk-brightRed text-xs">
              *{errors.Languages?.message}
            </span>
          )}
        </div>

        <div className="flex justify-center w-full">
          <button
            type="submit"
            className="rounded-full bg-employer-primary px-4 py-2 !text-spk-softBlue text-base font-semibold"
          >
            {t('EmployerAddPosition.ConfirmAndContinue')}
          </button>
        </div>
      </form>
    </>
  );
};

SPKPositionOverviewStep.defaultProps = {
  isInternal: false,
};

export default SPKPositionOverviewStep;
